// Portal-side field definitions per module. Mirrors the server's Zod
// schemas in services/api/src/modules/<id>/module.ts. Keep both in sync —
// a drift means either (a) a Zod schema accepts input the portal can't
// render, or (b) the portal renders inputs the server rejects.
//
// When a module's schema changes (version bump), add a conditional block
// here that picks the right field-def list by stored version so HR can
// still edit a company on an older version until they upgrade.

import type { FieldDef } from '../integrations/providers/field-defs.js';
import type { ModuleId } from './use-modules.js';

export interface ModuleFieldDefs {
  fields: FieldDef[];
}

export const MODULE_FIELDS: Record<ModuleId, ModuleFieldDefs> = {
  evl: {
    fields: [
      {
        kind: 'textarea',
        key: 'businessDescription',
        label: 'Business description paragraph',
        required: true,
        rows: 5,
        help: 'Inserted verbatim into every EVL. 20–2000 characters.',
      },
      {
        kind: 'checkbox',
        key: 'ccEmployeeOnDelivery',
        label: 'Cc the employee on every delivered letter',
      },
      {
        kind: 'text',
        key: 'highFrequencyThreshold',
        label: 'High-frequency threshold (count)',
        required: true,
        help: 'Hold for review if the same employee requests more than this many EVLs in the window.',
      },
      {
        kind: 'text',
        key: 'highFrequencyWindowDays',
        label: 'High-frequency window (days)',
        required: true,
      },
    ],
  },

  offer: {
    fields: [
      { kind: 'text', key: 'jiraProjectKey', label: 'Jira project key', required: true },
      {
        kind: 'text',
        key: 'hireTypeFieldId',
        label: 'Jira field id for hire type',
        required: true,
      },
      {
        kind: 'text',
        key: 'hireTypeEmployeeValue',
        label: 'Field value → "employee"',
        required: true,
      },
      {
        kind: 'text',
        key: 'hireTypeContractorValue',
        label: 'Field value → "contractor"',
        required: true,
      },
      {
        kind: 'text',
        key: 'onboardingSequenceId',
        label: 'BambooHR onboarding sequence id',
        required: true,
      },
    ],
  },

  termination: {
    fields: [
      {
        kind: 'checkbox',
        key: 'requireLegalCoSignerOnLegalFlag',
        label: 'Require second company_admin co-signer when legal flag is set',
      },
      {
        kind: 'text',
        key: 'reminderCadenceDays',
        label: 'Reminder cadence (comma-separated days)',
        help: 'Days after send to ping an unsigned envelope. e.g. "3,7"',
      },
    ],
  },
};
